import { ChatMessage } from "@metalmind-ab/mmagent-chat-ui";
import type { ChatMessageType } from "@metalmind-ab/mmagent-chat-ui";
import { render, screen } from "@testing-library/react";
import "@testing-library/jest-dom";
import {
  finalizeMyVaultAssistantMessage,
  settleMyVaultSend,
} from "./chatStreamFinalization";

const assistantMessage = (
  overrides: Partial<ChatMessageType> = {},
): ChatMessageType => ({
  id: "assistant-1",
  role: "assistant",
  content: "Working",
  createdAt: new Date("2026-10-09T00:00:00Z"),
  isStreaming: true,
  isWorking: true,
  toolExecutions: [
    {
      id: "tool-1",
      name: "create_view",
      args: {},
      status: "running",
    },
  ],
  ...overrides,
});

describe("finalizeMyVaultAssistantMessage", () => {
  it("finishes any tool still running when the send resolves", () => {
    const finalized = finalizeMyVaultAssistantMessage(
      assistantMessage(),
      "completed",
    );

    expect(finalized.isStreaming).toBe(false);
    expect(finalized.isWorking).toBe(false);
    expect(finalized.toolExecutions?.[0]).toMatchObject({
      id: "tool-1",
      status: "completed",
    });
  });

  it("finds the assistant after the done frame remaps its ID", () => {
    const createdAt = new Date("2026-10-09T00:00:00Z");
    const doneFrame = {
      type: "done",
      conversation_id: 9,
      user_message_id: 122,
      assistant_message_id: 123,
    };
    const remappedMessage = assistantMessage({
      id: String(doneFrame.assistant_message_id),
      createdAt,
    });

    const settlement = settleMyVaultSend(
      [remappedMessage],
      {
        generation: 1,
        contextVersion: 1,
        workspaceId: "workspace-1",
        assistantMessageCreatedAt: createdAt,
      },
      { generation: 1, contextVersion: 1, workspaceId: "workspace-1" },
      "completed",
    );

    expect(settlement.clearLoading).toBe(true);
    expect(settlement.messages[0].id).toBe("123");
    expect(settlement.messages[0].toolExecutions?.[0].status).toBe("completed");
  });

  it("marks a tool still running as failed with a visible error when send fails", () => {
    const failureMessage =
      "The chat stream failed before the tool result arrived.";
    const finalized = finalizeMyVaultAssistantMessage(
      assistantMessage(),
      "failed",
      failureMessage,
    );

    expect(finalized.isStreaming).toBe(false);
    expect(finalized.isWorking).toBe(false);
    expect(finalized.toolExecutions?.[0]).toMatchObject({
      id: "tool-1",
      status: "error",
      error: failureMessage,
    });

    render(<ChatMessage message={finalized} />);
    expect(screen.getByText("failed")).toHaveAttribute("title", failureMessage);
  });

  it("preserves tool outcomes already reported by the stream", () => {
    const finalized = finalizeMyVaultAssistantMessage(
      assistantMessage({
        toolExecutions: [
          {
            id: "tool-1",
            name: "create_view",
            args: {},
            status: "error",
            error: "Rejected by the server",
          },
        ],
      }),
      "completed",
    );

    expect(finalized.toolExecutions?.[0]).toMatchObject({
      status: "error",
      error: "Rejected by the server",
    });
  });

  it("ignores an old send after switching conversations", () => {
    const oldAssistantMessage = assistantMessage({
      id: "old-conversation-assistant",
    });
    const newConversationMessage = assistantMessage({
      id: "new-conversation-assistant",
    });
    const messages = [newConversationMessage];

    const settled = settleMyVaultSend(
      messages,
      {
        generation: 1,
        contextVersion: 1,
        workspaceId: "workspace-1",
        assistantMessageCreatedAt: oldAssistantMessage.createdAt,
      },
      { generation: 1, contextVersion: 2, workspaceId: "workspace-1" },
      "completed",
    );

    expect(settled.clearLoading).toBe(false);
    expect(settled.messages).toBe(messages);
    expect(settled.messages[0].toolExecutions?.[0].status).toBe("running");
  });

  it("does not let an old send settle a newer send in the same conversation", () => {
    const newAssistantMessage = assistantMessage({ id: "new-send-assistant" });
    const messages = [newAssistantMessage];

    const settled = settleMyVaultSend(
      messages,
      {
        generation: 1,
        contextVersion: 1,
        workspaceId: "workspace-1",
        assistantMessageCreatedAt: assistantMessage({
          id: "old-send-assistant",
        }).createdAt,
      },
      { generation: 2, contextVersion: 1, workspaceId: "workspace-1" },
      "completed",
    );

    expect(settled.clearLoading).toBe(false);
    expect(settled.messages).toBe(messages);
    expect(settled.messages[0].toolExecutions?.[0].status).toBe("running");
  });
});
